import React, { useState, useEffect, useCallback } from 'react';
import { deletePost, deleteComment, getReferralLeaderboard, getPosts, getPostComments } from '../services/authStore';

export default function Admin({ onBackClick }) {
  const [activeTab, setActiveTab] = useState('moderation');
  const [posts, setPosts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [postComments, setPostComments] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    if (activeTab === 'moderation') {
      const result = await getPosts();
      setPosts(Array.isArray(result) ? result : (result?.posts || []));
    } else {
      const data = await getReferralLeaderboard(20);
      setLeaderboard(data || []);
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeletePost = async (postId, postTitle) => {
    if (!window.confirm(`Delete post: "${postTitle}"? This action cannot be undone.`)) return;
    
    setDeletingId(postId);
    const result = await deletePost(postId);
    
    if (result.ok) {
      setPosts(posts.filter(p => p.id !== postId));
    } else {
      alert(`Error deleting post: ${result.error}`);
    }
    setDeletingId(null);
  };

  const handleExpandComments = async (postId) => {
    if (postComments[postId]) {
      setPostComments({ ...postComments, [postId]: null });
    } else {
      const comments = await getPostComments(postId);
      setPostComments({ ...postComments, [postId]: comments });
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment? This action cannot be undone.')) return;
    
    setDeletingId(commentId);
    const result = await deleteComment(commentId);
    
    if (result.ok) {
      // Refresh comment list for the post
      const updatedPostComments = {};
      for (const [postId, comments] of Object.entries(postComments)) {
        updatedPostComments[postId] = comments?.filter(c => c.id !== commentId) || [];
      }
      setPostComments(updatedPostComments);
    } else {
      alert(`Error deleting comment: ${result.error}`);
    }
    setDeletingId(null);
  };

  return (
    <div className="min-h-screen bg-[#121212]">
      {/* Back button and title */}
      <div className="border-b border-[#333] bg-[#0a0a0a] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
          <button
            onClick={onBackClick}
            className="text-[#50fa7b] hover:opacity-80 text-sm font-mono min-h-[44px] flex items-center touch-manipulation"
          >
            ← Back to Home
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Admin Panel</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#333] bg-[#0a0a0a] sticky top-14 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-4 sm:gap-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('moderation')}
            className={`py-3 px-3 min-h-[44px] border-b-2 font-mono text-sm transition whitespace-nowrap touch-manipulation ${
              activeTab === 'moderation'
                ? 'border-[#50fa7b] text-[#50fa7b]'
                : 'border-transparent text-[#999] hover:text-white'
            }`}
          >
            Moderation ({posts.length})
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`py-3 px-3 min-h-[44px] border-b-2 font-mono text-sm transition whitespace-nowrap touch-manipulation ${
              activeTab === 'leaderboard'
                ? 'border-[#50fa7b] text-[#50fa7b]'
                : 'border-transparent text-[#999] hover:text-white'
            }`}
          >
            Referral Leaderboard
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-2 border-[#50fa7b] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : activeTab === 'moderation' ? (
          // Moderation Tab
          <div className="space-y-4">
            {posts.length === 0 ? (
              <p className="text-[#999] text-center py-8">No posts to moderate</p>
            ) : (
              posts.map((post) => (
                <div key={post.id} className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 hover:border-[#50fa7b] transition">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-white mb-1">{post.title}</h3>
                      <p className="text-[#999] text-sm mb-2 line-clamp-2">{post.content}</p>
                      <div className="flex items-center gap-3 text-xs text-[#666]">
                        <span className="bg-[#2a2a2a] px-2 py-1 rounded">{post.category}</span>
                        <span>{post.comment_count} comments</span>
                        <button
                          onClick={() => handleExpandComments(post.id)}
                          className="text-[#50fa7b] hover:opacity-80 py-2 touch-manipulation"
                        >
                          {postComments[post.id] ? 'Hide comments' : 'Show comments'}
                        </button>
                      </div>

                      {/* Comments List */}
                      {postComments[post.id] && (
                        <div className="mt-4 pl-4 border-l border-[#333] space-y-2">
                          {(postComments[post.id] || []).length === 0 ? (
                            <p className="text-[#666] text-xs">No comments</p>
                          ) : (
                            (postComments[post.id] || []).map((comment) => (
                              <div key={comment.id} className="text-xs text-[#999] bg-[#0a0a0a] p-2 rounded flex justify-between gap-2">
                                <span className="line-clamp-1">{comment.content}</span>
                                <button
                                  onClick={() => handleDeleteComment(comment.id)}
                                  disabled={deletingId === comment.id}
                                  className="text-red-500 hover:text-red-400 disabled:opacity-50 flex-shrink-0 py-2 touch-manipulation"
                                >
                                  {deletingId === comment.id ? 'Deleting...' : 'Delete'}
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeletePost(post.id, post.title)}
                      disabled={deletingId === post.id}
                      className="text-red-500 hover:text-red-400 disabled:opacity-50 font-mono text-sm flex-shrink-0 min-h-[44px] px-3 touch-manipulation"
                    >
                      {deletingId === post.id ? 'Deleting...' : 'Delete Post'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          // Leaderboard Tab
          <div className="space-y-3">
            {leaderboard.length === 0 ? (
              <p className="text-[#999] text-center py-8">No referrals yet</p>
            ) : (
              leaderboard.map((item, idx) => (
                <div key={item.userId} className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 hover:border-[#50fa7b] transition">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#50fa7b] text-[#121212] flex items-center justify-center font-bold text-sm">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-bold text-white">{item.name}</p>
                        <p className="text-xs text-[#999]">{item.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#50fa7b] text-lg">{item.count}</p>
                      <p className="text-xs text-[#999]">referrals</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
